import {
  type Chart,
  type ChartOptions,
  type ScriptableTooltipContext,
  type ChartData,
} from "chart.js";
import { compact } from "lodash";

import {
  debouncedResize,
  onCustomAnnotationClick,
  onCustomAnnotationHover,
} from "@features/sales-v3/utils/chart";
import { type User } from "@internal/user";
import { MetricFormatters } from "@north-beam/nb-client-side";

import { KpiType } from "../../explore-budgets/client-setting/types";

import { type ChartDataPoint } from "./model-fit-chart";
import { chartTooltip } from "./model-fit-chart-tooltip";
import {
  type YScaleTitlesPluginPluginOptions,
  Y_SCALE_TITLES_PLUGIN_ID,
} from "./plugins/y-scale-titles-plugin";
import {
  inSampleAnnotations,
  outOfSampleAnnotations,
} from "./utils/build-chart-annotations";
import { MODEL_FIT_SPEND_ID } from "./utils/chart-dataset-helpers";

export const getChartOptions = (
  user: User,
  tooltipTitle: string,
  tooltipHdiProb: number,
  kpiType: KpiType,
  data: ChartData<any, ChartDataPoint[], unknown>,
): ChartOptions<any> => {
  const isRevenue = kpiType === KpiType.REVENUE;

  const shouldAlignAxes = isRevenue;
  const unhiddenDatasets = data.datasets.filter((dataset) => !dataset.hidden);
  const maxYValue = Math.max(
    ...compact(
      unhiddenDatasets
        .flatMap((dataset) => dataset.data)
        .flatMap((datasetData) => datasetData.y),
    ),
  );
  const suggestedMaxOption = shouldAlignAxes
    ? {
        suggestedMax: maxYValue,
      }
    : {};

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
      position: "nearest",
    },
    plugins: {
      annotation: {
        annotations: {
          ...inSampleAnnotations,
          ...outOfSampleAnnotations,
        },
      },
      legend: { display: false },
      zoom: {
        limits: {
          x: { min: "original", max: "original" },
        },
        zoom: {
          wheel: {
            modifierKey: "alt",
            enabled: true,
          },
          drag: {
            modifierKey: "alt",
            enabled: true,
          },
          mode: "x",
        },
      },
      tooltip: {
        enabled: false,
        external: (ctx: ScriptableTooltipContext<"line">) =>
          chartTooltip(ctx, kpiType),
        tooltipTitle,
        tooltipHdiProb,
        user,
      },
    },
    layout: { padding: { top: 28, bottom: 36, left: 16, right: 16 } },
    [Y_SCALE_TITLES_PLUGIN_ID]: {
      font: {
        family: "Inter",
        size: 12,
        weight: "600",
      },
      titles: {},
    } as YScaleTitlesPluginPluginOptions,
    scales: {
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        grace: "50%",
        beginAtZero: true,
        min: 0,
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: true,
        },
        ticks: {
          maxTicksLimit: 6,
          padding: 12,
          font: {
            family: "Commons Pro Mono",
            size: 11,
          },
          autoSkip: true,
          callback: (val: string) =>
            isRevenue
              ? MetricFormatters.currency(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                )
              : MetricFormatters.decimal(
                  Number(val),
                  user.currencies.currentCurrency,
                  {
                    precision: "approximate",
                  },
                ),
        },
        ...suggestedMaxOption,
      },
      x: {
        axis: "x",
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
          drawTicks: false,
        },
        ticks: {
          padding: 20,
          maxRotation: 0,
          autoSkip: true,
          font: {
            family: "Commons Pro",
            size: 13,
          },
        },
        type: "timeseries",
        time: { unit: "week", isoWeekday: 2 },
      },
      [MODEL_FIT_SPEND_ID]: {
        type: "linear" as const,
        suggestedMax: 5,
        axis: "y",
        position: "right" as const,
        grace: "50%",
        beginAtZero: true,
        title: {
          display: false,
          padding: 0,
        },
        grid: {
          drawTicks: false,
          drawBorder: false,
          drawOnChartArea: true,
        },
        ticks: {
          maxTicksLimit: 6,
          font: {
            family: "Commons Pro Mono",
            size: 11,
          },
          callback: (val: string) =>
            MetricFormatters.currency(
              Number(val),
              user.currencies.currentCurrency,
              {
                precision: "approximate",
              },
            ),
        },
        ...suggestedMaxOption,
      },
    },
    onResize(chart: Chart) {
      // on resize, reload the annotations
      debouncedResize(chart);
    },
    onHover: onCustomAnnotationHover,
    onClick: onCustomAnnotationClick,
  };
};
